import { ColDef, ColGroupDef, GridOptions } from 'ag-grid-community'
import { CollateralDetail } from 'api'
import { Locale, Translation } from 'locale'
import {
  createActionsRenderer,
  createGroupedActionRenderer,
  dateFilterParams,
  gridOptionsBase,
  groupingRowColDef,
  StatusCellRenderer,
  TypedValueGetterParams
} from 'view/components/datagrid'
import {
  booleanFormatter,
  collateralTypeFormatter,
  complianceValidFormatter,
  couponFrequencyFormatter,
  couponFrequencyQuickFilterTextGetter,
  couponTypeFormatter,
  dateQuickFilterTextGetter,
  getCollateralStatusActiveSuspendedText,
  getCollateralStatusColor,
  indexLagFormatter,
  makeDateAndTimeFormatter,
  makeDateFormatter,
  makeNumberFilterGetter,
  makeNumberFormatter,
  makeNumberQuickFilterTextGetter,
  makeTimestampFormatter
} from 'view/shared/datagrid'
import { CollateralActionsButton } from './CollateralActionsButton'
import { CollateralIdentifierTypeHelper, TradingTypeHelper } from 'model'
import { compareInsensitive } from 'utils'
import { BASE_ASSET_TYPE, BASE_ISSUE } from 'view/shared/datagrid/baseColumns'

enum CollateralGridColumnId {
  COLLATERAL_CHECKBOX = 'COLLATERAL_CHECKBOX',
  COLLATERAL_ACTIONS = 'COLLATERAL_ACTIONS',
  COLLATERAL_STATUS = 'COLLATERAL_STATUS',
  COLLATERAL_NAME = 'COLLATERAL_NAME',
  COLLATERAL_EU_COMPLIANT = 'COLLATERAL_EU_COMPLIANT',
  COLLATERAL_ID = 'COLLATERAL_ID',
  COLLATERAL_PLATFORM = 'COLLATERAL_PLATFORM',
  COLLATERAL_ISIN = 'COLLATERAL_ISIN',
  COLLATERAL_FISN = 'COLLATERAL_FISN',
  COLLATERAL_CFI = 'COLLATERAL_CFI',
  COLLATERAL_CUSIP = 'COLLATERAL_CUSIP',
  COLLATERAL_CURRENCY = 'COLLATERAL_CURRENCY',
  COLLATERAL_COUNTRY = 'COLLATERAL_COUNTRY',
  COLLATERAL_SECTOR = 'COLLATERAL_SECTOR',
  COLLATERAL_PRODUCT = 'COLLATERAL_PRODUCT',
  COLLATERAL_ISSUER = 'COLLATERAL_ISSUER',
  COLLATERAL_TRADING_TYPE = 'COLLATERAL_TRADING_TYPE',
  COLLATERAL_FIRST_SETTLEMENT_DATE = 'COLLATERAL_FIRST_SETTLEMENT_DATE',
  COLLATERAL_PRICE_DIRTY = 'COLLATERAL_PRICE_DIRTY',
  COLLATERAL_PRICE_CLEAN = 'COLLATERAL_PRICE_CLEAN',
  COLLATERAL_PRICE_UPDATED = 'COLLATERAL_PRICE_UPDATED',
  COLLATERAL_MINIMUM_TICKET_SIZE = 'COLLATERAL_MINIMUM_TICKET_SIZE',
  COLLATERAL_TICKET_SIZE = 'COLLATERAL_TICKET_SIZE',
  COLLATERAL_BASIS_CONVENTION = 'COLLATERAL_BASIS_CONVENTION',
  COLLATERAL_COUPON = 'COLLATERAL_COUPON',
  COLLATERAL_COUPON_TYPE = 'COLLATERAL_COUPON_TYPE',
  COLLATERAL_COUPON_ACCRUAL_DATE = 'COLLATERAL_COUPON_ACCRUAL_DATE',
  COLLATERAL_COUPON_FIRST_DATE = 'COLLATERAL_COUPON_FIRST_DATE',
  COLLATERAL_COUPON_MATURE_DATE = 'COLLATERAL_COUPON_MATURE_DATE',
  COLLATERAL_COUPON_FREQUENCY = 'COLLATERAL_COUPON_FREQUENCY',
  COLLATERAL_COLLATERAL_TYPE = 'COLLATERAL_COLLATERAL_TYPE',
  COLLATERAL_INDEX = 'COLLATERAL_INDEX',
  COLLATERAL_LAG = 'COLLATERAL_LAG',
  COLLATERAL_BASE_INDEX_VALUE = 'COLLATERAL_BASE_INDEX_VALUE',
  COLLATERAL_PROSPECTUS = 'COLLATERAL_PROSPECTUS',
  COLLATERAL_FACTOR = 'COLLATERAL_FACTOR',
  COLLATERAL_FACTOR_DATE = 'COLLATERAL_FACTOR_DATE',
  COLLATERAL_MBS_PRICE_DATE = 'COLLATERAL_MBS_PRICE_DATE',
  COLLATERAL_COUNTRY_OF_INCORPORATION = 'COLLATERAL_COUNTRY_OF_INCORPORATION',
  COLLATERAL_COUNTRY_OF_ISSUANCE = 'COLLATERAL_COUNTRY_OF_ISSUANCE',
  COLLATERAL_REFINITIV_ASSET_TYPE = 'COLLATERAL_REFINITIV_ASSET_TYPE',
  COLLATERAL_ASSET_SUB_TYPE = 'COLLATERAL_ASSET_SUB_TYPE',
  COLLATERAL_REFINITIV_BASE_INDEX = 'COLLATERAL_REFINITIV_BASE_INDEX',
  COLLATERAL_DEBT_TYPE = 'COLLATERAL_DEBT_TYPE',
  ISSUER_LEI = 'ISSUER_LEI',
  FIRST_TRADE_DATE = 'FIRST_TRADE_DATE',
  TOTAL_ISSUED_NOMINAL_AMOUNT = 'TOTAL_ISSUED_NOMINAL_AMOUNT',
  INDEX_SPREAD = 'INDEX_SPREAD',
  SENIORITY = 'SENIORITY',
  RIC = 'RIC',
  LAST_UPDATED_DATE = 'LAST_UPDATED_DATE',
  LAST_UPDATED_BY = 'LAST_UPDATED_BY',
  ASSET_TYPE = 'ASSET_TYPE',
  ASSET_STATUS = 'ASSET_STATUS',
  ASSET_STATUS_EFFECTIVE_DATE = 'ASSET_STATUS_EFFECTIVE_DATE',
  RECOGNISED_EXCHANGE = 'RECOGNISED_EXCHANGE',
  IN_ESMA_FIRDS = 'IN_ESMA_FIRDS',
  EEA_VENUE_ELIGIBLE = 'EEA_VENUE_ELIGIBLE',
  UK_VENUE_ELIGIBLE = 'UK_VENUE_ELIGIBLE',
  RULE_144A_INDICATOR = 'RULE_144A_INDICATOR',
  ISSUER_ORG_ID = 'ISSUER_ORG_ID',
  COMPLIANCE_OVERRIDE = 'COMPLIANCE_OVERRIDE',
  ISSUE = 'ISSUE'
}

export const CollateralColumnDefs: (ColGroupDef | ColDef)[] = [
  {
    colId: CollateralGridColumnId.COLLATERAL_CHECKBOX,
    headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
    headerCheckboxSelection: true,
    headerCheckboxSelectionFilteredOnly: true,
    checkboxSelection: true,
    width: 36,
    maxWidth: 36,
    pinned: 'left',
    lockPinned: true,
    enableRowGroup: false,
    lockPosition: true,
    lockVisible: true,
    filter: false,
    resizable: false,
    sortable: false
  },
  {
    ...groupingRowColDef,
    headerName: Locale.translate(Translation.TC_SECTOR),
    width: 100,
    showRowGroup: 'COLLATERAL_SECTOR',
    resizable: false,
    pinned: 'left',
    lockPinned: true,
    lockVisible: true
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_ACTIONS,
    headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
    equals: () => false,
    width: 130,
    minWidth: 130,
    headerComponent: createGroupedActionRenderer(CollateralActionsButton),
    cellClass: 'action-cell',
    cellRenderer: createActionsRenderer(CollateralActionsButton),
    pinned: 'left',
    lockPinned: true,
    enableRowGroup: false,
    lockVisible: true,
    filter: false,
    sortable: false
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_STATUS,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) =>
      params.data && getCollateralStatusActiveSuspendedText(params.data.collateral.status),
    headerName: Locale.translate(Translation.TC_STATUS),
    width: 120,
    filter: 'agSetColumnFilter',
    cellRenderer: StatusCellRenderer,
    cellRendererParams: {
      getStatusColor: (data: CollateralDetail) => getCollateralStatusColor(data.collateral.status)
    },
    equals: () => false,
    lockVisible: true,
    lockPosition: true
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_EU_COMPLIANT,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => complianceValidFormatter(params.data?.collateral.euCompliant),
    headerName: Locale.translate(Translation.TC_COMPLIANCE_EUROPE),
    width: 120,
    filter: 'agSetColumnFilter'
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_NAME,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.name,
    headerName: Locale.translate(Translation.TC_SHORT_ID),
    width: 120,
    filter: 'agSetColumnFilter'
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_ISIN,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.isin,
    headerName: Locale.translate(Translation.TC_ISIN),
    width: 120,
    filter: 'agSetColumnFilter'
  },
  {
    colId: CollateralGridColumnId.RIC,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.ric,
    headerName: Locale.translate(Translation.SC_RIC),
    width: 120,
    filter: 'agSetColumnFilter'
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_FISN,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.fisin,
    headerName: Locale.translate(Translation.TC_FISN),
    width: 120,
    filter: 'agSetColumnFilter'
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_CFI,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.cfi,
    headerName: Locale.translate(Translation.TC_CFI),
    width: 120,
    filter: 'agSetColumnFilter'
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_CUSIP,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.cusip,
    headerName: Locale.translate(Translation.TC_CUSIP),
    width: 120,
    filter: 'agSetColumnFilter'
  },
  {
    ...BASE_ASSET_TYPE,
    colId: CollateralGridColumnId.ASSET_TYPE,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.assetType,
    width: 120
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_CURRENCY,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.currency,
    headerName: Locale.translate(Translation.TC_CURRENCY),
    width: 120,
    filter: 'agSetColumnFilter'
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_COUNTRY,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.countryCode,
    headerName: Locale.translate(Translation.TC_COUNTRY),
    width: 120,
    filter: 'agSetColumnFilter'
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_SECTOR,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.productSector?.name,
    headerName: Locale.translate(Translation.TC_SECTOR),
    width: 120,
    filter: 'agSetColumnFilter',
    rowGroup: true
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_PRODUCT,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.product?.name,
    headerName: Locale.translate(Translation.TC_PRODUCT),
    width: 120,
    filter: 'agSetColumnFilter'
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_ISSUER,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.issuer,
    headerName: Locale.translate(Translation.TC_ISSUER),
    width: 120,
    filter: 'agSetColumnFilter'
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_TRADING_TYPE,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) =>
      params.data && TradingTypeHelper.getName(params.data.collateral.tradingType),
    headerName: Locale.translate(Translation.TC_TRADING_TYPE),
    width: 120,
    filter: 'agSetColumnFilter'
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_FIRST_SETTLEMENT_DATE,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.issueDate,
    headerName: Locale.translate(Translation.TC_FIRST_SETTLEMENT_DATE),
    width: 120,
    filter: 'agDateColumnFilter',
    ...dateFilterParams,
    valueFormatter: makeDateFormatter()
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_PRICE_DIRTY,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.dirtyPrice,
    headerName: Locale.translate(Translation.TC_DIRTY_PRICE),
    width: 120,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: makeNumberFormatter(),
    filterValueGetter: makeNumberFilterGetter()
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_PRICE_CLEAN,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateralPrice?.cleanPrice,
    headerName: Locale.translate(Translation.TC_CLEAN_PRICE),
    width: 120,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: makeNumberFormatter(),
    filterValueGetter: makeNumberFilterGetter()
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_PRICE_UPDATED,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateralPrice?.lastCleanPriceUpdate,
    headerName: Locale.translate(Translation.TC_CLEAN_PRICE_UPDATED),
    width: 120,
    filter: 'agDateColumnFilter',
    ...dateFilterParams,
    valueFormatter: makeTimestampFormatter(),
    getQuickFilterText: dateQuickFilterTextGetter
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_MINIMUM_TICKET_SIZE,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.minimumTicketSize,
    headerName: Locale.translate(Translation.TC_MINIMUM_TICKET_SIZE),
    width: 120,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: makeNumberFormatter(),
    filterValueGetter: makeNumberFilterGetter()
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_TICKET_SIZE,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.ticketIncrement,
    headerName: Locale.translate(Translation.TC_TICKET_SIZE_INCREMENT),
    width: 120,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: makeNumberFormatter(),
    filterValueGetter: makeNumberFilterGetter()
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_BASIS_CONVENTION,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.basisConvention,
    headerName: Locale.translate(Translation.TC_BASIS_CONVENTION),
    width: 120,
    filter: 'agSetColumnFilter'
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_COUPON_TYPE,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.couponType,
    headerName: Locale.translate(Translation.TC_COUPON_TYPE),
    width: 120,
    filter: 'agSetColumnFilter',
    valueFormatter: couponTypeFormatter
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_COUPON,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.coupon,
    headerName: Locale.translate(Translation.TC_COUPON),
    width: 120,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: makeNumberFormatter({ percent: true, minDp: 1, decimalShift: 2 }),
    filterValueGetter: makeNumberFilterGetter({ percent: true, minDp: 1, decimalShift: 2 }),
    getQuickFilterText: makeNumberQuickFilterTextGetter({ percent: true })
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_COUPON_ACCRUAL_DATE,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.couponAccruedInterestDate,
    headerName: Locale.translate(Translation.TC_ACCRUED_INTEREST_DATE),
    width: 120,
    filter: 'agDateColumnFilter',
    ...dateFilterParams,
    valueFormatter: makeDateFormatter(),
    getQuickFilterText: dateQuickFilterTextGetter
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_COUPON_FIRST_DATE,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.couponFirstDate,
    headerName: Locale.translate(Translation.TC_FIRST_COUPON_DATE),
    width: 120,
    filter: 'agDateColumnFilter',
    ...dateFilterParams,
    valueFormatter: makeDateFormatter(),
    getQuickFilterText: dateQuickFilterTextGetter
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_COUPON_MATURE_DATE,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.couponMaturityDate,
    headerName: Locale.translate(Translation.TC_MATURITY_DATE),
    width: 120,
    filter: 'agDateColumnFilter',
    ...dateFilterParams,
    valueFormatter: makeDateFormatter(),
    getQuickFilterText: dateQuickFilterTextGetter
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_COUPON_FREQUENCY,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.couponFrequency,
    headerName: Locale.translate(Translation.TC_FREQUENCY),
    width: 120,
    filter: 'agSetColumnFilter',
    valueFormatter: couponFrequencyFormatter,
    getQuickFilterText: couponFrequencyQuickFilterTextGetter
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_COLLATERAL_TYPE,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.collateralType,
    valueFormatter: collateralTypeFormatter,
    headerName: Locale.translate(Translation.TC_COLLATERAL_TYPE),
    width: 120,
    filter: 'agSetColumnFilter'
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_FACTOR,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.factor,
    headerName: Locale.translate(Translation.TC_FACTOR),
    width: 120,
    filter: 'agSetColumnFilter'
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_FACTOR_DATE,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.factorDate,
    headerName: Locale.translate(Translation.TC_FACTOR_DATE),
    width: 120,
    filter: 'agDateColumnFilter',
    ...dateFilterParams,
    valueFormatter: makeDateFormatter(),
    getQuickFilterText: dateQuickFilterTextGetter
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_MBS_PRICE_DATE,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.mbsPriceDate,
    headerName: Locale.translate(Translation.TC_MBS_PRICE_DATE),
    width: 120,
    filter: 'agDateColumnFilter',
    ...dateFilterParams,
    valueFormatter: makeDateFormatter(),
    getQuickFilterText: dateQuickFilterTextGetter
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_INDEX,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.index?.code,
    headerName: Locale.translate(Translation.TC_INDEX),
    width: 120,
    filter: 'agSetColumnFilter'
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_LAG,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.collateralIndexDetails?.indexLag,
    valueFormatter: indexLagFormatter,
    headerName: Locale.translate(Translation.TC_INDEX_LAG),
    width: 120,
    filter: 'agSetColumnFilter'
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_BASE_INDEX_VALUE,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.collateralIndexDetails?.baseIndexValue,
    headerName: Locale.translate(Translation.TC_BASE_INDEX_VALUE),
    width: 120,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter'
  },
  {
    colId: CollateralGridColumnId.ASSET_STATUS,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.assetStatus,
    headerName: Locale.translate(Translation.TC_ASSET_STATUS),
    width: 120,
    filter: 'agSetColumnFilter',
    hide: true
  },
  {
    colId: CollateralGridColumnId.ASSET_STATUS_EFFECTIVE_DATE,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.assetStatusEffectiveDate,
    headerName: Locale.translate(Translation.TC_ASSET_STATUS_EFFECTIVE_DATE),
    filter: 'agDateColumnFilter',
    ...dateFilterParams,
    valueFormatter: makeDateFormatter(),
    getQuickFilterText: dateQuickFilterTextGetter,
    width: 120,
    hide: true
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_ID,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.id,
    headerName: Locale.translate(Translation.TC_COLLATERAL_ID),
    width: 120,
    hide: true
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_PLATFORM,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) =>
      params.data && CollateralIdentifierTypeHelper.getName(params.data.collateral.collateralIdentifierType),
    headerName: Locale.translate(Translation.SC_PLATFORM),
    width: 120,
    hide: true
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_PROSPECTUS,
    valueFormatter: booleanFormatter,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.prospectus,
    headerName: Locale.translate(Translation.SC_PROSPECTUS),
    width: 120
  },
  {
    colId: CollateralGridColumnId.ISSUER_LEI,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.issuerLei,
    headerName: Locale.translate(Translation.TC_ISSUER_LEI),
    width: 120
  },
  {
    colId: CollateralGridColumnId.FIRST_TRADE_DATE,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.firstTradeTimestamp,
    headerName: Locale.translate(Translation.SC_FIRST_TRADE_DATE),
    width: 140,
    filter: 'agDateColumnFilter',
    ...dateFilterParams,
    valueFormatter: makeDateAndTimeFormatter(),
    getQuickFilterText: dateQuickFilterTextGetter
  },
  {
    colId: CollateralGridColumnId.TOTAL_ISSUED_NOMINAL_AMOUNT,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.totalIssuedNominalAmount,
    headerName: Locale.translate(Translation.SC_TOTAL_ISSUED_NOMINAL_AMOUNT),
    width: 120
  },
  {
    colId: CollateralGridColumnId.INDEX_SPREAD,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.indexSpread,
    headerName: Locale.translate(Translation.SC_INDEX_SPREAD),
    width: 120
  },
  {
    colId: CollateralGridColumnId.SENIORITY,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.seniority,
    headerName: Locale.translate(Translation.SC_SENIORITY),
    width: 120
  },
  {
    colId: CollateralGridColumnId.LAST_UPDATED_DATE,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.lastUpdatedAtByUser,
    headerName: Locale.translate(Translation.TC_LAST_UPDATED_AT_DATE_AND_TIME),
    width: 140,
    filter: 'agDateColumnFilter',
    ...dateFilterParams,
    valueFormatter: makeDateAndTimeFormatter(),
    getQuickFilterText: dateQuickFilterTextGetter
  },
  {
    colId: CollateralGridColumnId.LAST_UPDATED_BY,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.lastUpdatedByUserName,
    headerName: Locale.translate(Translation.TC_LAST_UPDATED_BY),
    width: 140
  },
  {
    colId: CollateralGridColumnId.IN_ESMA_FIRDS,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.inEsmaFirds,
    headerName: Locale.translate(Translation.TC_IN_ESMA_FIRDS),
    width: 140,
    valueFormatter: booleanFormatter,
    hide: true
  },
  {
    colId: CollateralGridColumnId.EEA_VENUE_ELIGIBLE,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.eeaVenueEligible,
    headerName: Locale.translate(Translation.TC_EEA_VENUE_ELIGIBLE),
    width: 140,
    valueFormatter: booleanFormatter,
    hide: true
  },
  {
    colId: CollateralGridColumnId.UK_VENUE_ELIGIBLE,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.ukVenueEligible,
    headerName: Locale.translate(Translation.TC_UK_VENUE_ELIGIBLE),
    width: 140,
    valueFormatter: booleanFormatter,
    hide: true
  },
  {
    colId: CollateralGridColumnId.RULE_144A_INDICATOR,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.rule144aIndicator,
    headerName: Locale.translate(Translation.TC_RULE_144A_INDICATOR),
    width: 140,
    valueFormatter: booleanFormatter,
    hide: true
  },
  {
    colId: CollateralGridColumnId.ISSUER_ORG_ID,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.issuerOrgId,
    headerName: Locale.translate(Translation.TC_ISSUER_ORG_ID),
    width: 140,
    hide: true
  },
  {
    colId: CollateralGridColumnId.COMPLIANCE_OVERRIDE,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.complianceOverride,
    headerName: Locale.translate(Translation.TC_COMPLIANCE_OVERRIDE),
    width: 140,
    valueFormatter: booleanFormatter,
    hide: true
  },
  {
    ...BASE_ISSUE,
    colId: CollateralGridColumnId.ISSUE,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.issue,
    width: 140,
    hide: true
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_COUNTRY_OF_INCORPORATION,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.countryOfIncorporation,
    headerName: Locale.translate(Translation.TC_COUNTRY_OF_INCORPORATION),
    width: 140,
    hide: true
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_COUNTRY_OF_ISSUANCE,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.countryOfIssuance,
    headerName: Locale.translate(Translation.TC_COUNTRY_OF_ISSUANCE),
    width: 140,
    filter: 'agSetColumnFilter',
    hide: true
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_REFINITIV_ASSET_TYPE,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.refinitivAssetType,
    headerName: Locale.translate(Translation.TC_REFINITIV_ASSET_TYPE),
    width: 140,
    filter: 'agSetColumnFilter',
    hide: true
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_REFINITIV_BASE_INDEX,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.refinitivBaseIndex,
    headerName: Locale.translate(Translation.TC_REFINITIV_BASE_INDEX),
    width: 140,
    filter: 'agSetColumnFilter',
    hide: true
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_ASSET_SUB_TYPE,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.assetSubType,
    headerName: Locale.translate(Translation.TC_ASSET_SUB_TYPE),
    width: 140,
    filter: 'agSetColumnFilter',
    hide: true
  },
  {
    colId: CollateralGridColumnId.COLLATERAL_DEBT_TYPE,
    valueGetter: (params: TypedValueGetterParams<CollateralDetail>) => params.data?.collateral.debtType,
    headerName: Locale.translate(Translation.TC_ASSET_DEBT_TYPE),
    width: 140,
    filter: 'agSetColumnFilter',
    hide: true
  }
]

export const collateralGridOptions: GridOptions = {
  ...gridOptionsBase,
  groupDefaultExpanded: 0,
  getRowId: ({ data }: { data: CollateralDetail }) => applyTransactionGetId(data),
  initialGroupOrderComparator: ({ nodeA, nodeB }) => compareInsensitive(nodeA.key ?? '', nodeB.key ?? '')
}

export function applyTransactionGetId(collateralDetail: CollateralDetail) {
  return collateralDetail.collateral.id
}
